import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setCurrentFolder } from '../../../../store/reducers/dropbox';
import { toast } from 'react-toastify';
import * as dropboxService from '../../../../services/dropbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFolder, 
  faFolderOpen, 
  faStar, 
  faClock, 
  faHome,
  faChevronLeft,
  faChevronDown,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import ConfirmMoveModal from '../Modals/ConfirmMoveModal';

interface DropboxSidebarProProps {
  onFolderClick: (folderId: string | null) => void;
  onViewChange?: (view: 'all' | 'favorites' | 'recent' | 'trash') => void;
  onFolderMoved?: () => void;
  currentView?: 'all' | 'favorites' | 'recent' | 'trash';
}

const DropboxSidebarPro: React.FC<DropboxSidebarProProps> = ({ onFolderClick, onViewChange, onFolderMoved, currentView = 'all' }) => {
  const dispatch = useDispatch();
  const { currentFolderId, folders } = useSelector((state: any) => state.dropbox);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [draggedFolder, setDraggedFolder] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [showConfirmMove, setShowConfirmMove] = useState(false);
  const [moveData, setMoveData] = useState<{ sourceId: string; targetId: string } | null>(null);
  const [isMoving, setIsMoving] = useState(false);

  // Calculate which folders should be expanded based on current folder
  const calculatedExpandedFolders = useMemo(() => {
    if (!currentFolderId) {
      return new Set<string>();
    }

    const newExpanded = new Set<string>();
    
    // Build the path from current folder to root
    const buildPath = (folderId: string) => {
      const folder = folders.find((f: any) => f.id === folderId);
      if (folder && folder.parent_id) {
        newExpanded.add(folder.parent_id);
        buildPath(folder.parent_id);
      }
    };
    
    buildPath(currentFolderId);
    return newExpanded;
  }, [currentFolderId, folders]);

  // Update expanded folders when calculation changes
  useEffect(() => {
    setExpandedFolders(calculatedExpandedFolders);
  }, [calculatedExpandedFolders]);

  // Check if a folder is in the active path (ancestor of current folder)
  const isInActivePath = useCallback((folderId: string): boolean => {
    if (!currentFolderId || currentFolderId === folderId) return false;
    
    // Check if this folder is an ancestor of the current folder
    let checkId = currentFolderId;
    while (checkId) {
      const folder = folders.find((f: any) => f.id === checkId);
      if (!folder || !folder.parent_id) break;
      if (folder.parent_id === folderId) return true;
      checkId = folder.parent_id;
    }
    return false;
  }, [currentFolderId, folders]);

  const handleViewClick = (view: 'all' | 'favorites' | 'recent' | 'trash') => {
    dispatch(setCurrentFolder(null));
    if (onViewChange) onViewChange(view);
  };

  const handleFolderClick = (folderId: string, hasChildren: boolean) => {
    // Only set view to 'all' if we're not already in it
    if (currentView !== 'all') {
      if (onViewChange) {
        onViewChange('all');
      }
    }
    
    dispatch(setCurrentFolder(folderId));
    onFolderClick(folderId);
  };

  const handleFolderDoubleClick = (folderId: string, hasChildren: boolean) => {
    handleFolderClick(folderId, hasChildren);
  };

  const toggleFolderExpand = useCallback((folderId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedFolders(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(folderId)) {
        newExpanded.delete(folderId);
      } else {
        newExpanded.add(folderId);
      }
      return newExpanded;
    });
  }, []);

  const handleDragStart = (folderId: string, e: React.DragEvent) => {
    e.stopPropagation();
    setDraggedFolder(folderId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('folderId', folderId);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    e.preventDefault();
    // Reset drag state when drag ends (whether dropped or cancelled)
    setDraggedFolder(null);
    setDropTarget(null);
  };

  const handleDragOver = (folderId: string, e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedFolder && draggedFolder !== folderId) {
      e.dataTransfer.dropEffect = 'move';
      setDropTarget(folderId);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDropTarget(null);
  };

  const handleDrop = async (targetFolderId: string, e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const sourceFolderId = e.dataTransfer.getData('folderId');
    
    if (!sourceFolderId || sourceFolderId === targetFolderId) {
      setDraggedFolder(null);
      setDropTarget(null);
      return;
    }

    // Check if source folder's parent is already the target (no move needed)
    const sourceFolder = folders.find((f: any) => f.id === sourceFolderId);
    if (sourceFolder && sourceFolder.parent_id === targetFolderId) {
      setDraggedFolder(null);
      setDropTarget(null);
      return;
    }

    // Check if trying to move folder into its own subfolder
    const isDescendant = (parentId: string, childId: string): boolean => {
      const folder = folders.find((f: any) => f.id === childId);
      if (!folder || !folder.parent_id) return false;
      if (folder.parent_id === parentId) return true;
      return isDescendant(parentId, folder.parent_id);
    };

    if (isDescendant(sourceFolderId, targetFolderId)) {
      toast.error('Cannot move a folder into its own subfolder');
      setDraggedFolder(null);
      setDropTarget(null);
      return;
    }

    // Show confirmation modal
    setMoveData({ sourceId: sourceFolderId, targetId: targetFolderId });
    setShowConfirmMove(true);
    setDraggedFolder(null);
    setDropTarget(null);
  };

  const handleConfirmMove = async () => {
    if (!moveData) return;

    setIsMoving(true);
    try {
      const response = await dropboxService.moveFolder(moveData.sourceId, moveData.targetId);
      if (response?.success) {
        toast.success('Folder moved successfully');
        if (onFolderMoved) onFolderMoved();
      } else {
        toast.error('Failed to move folder');
      }
    } catch (error) {
      toast.error('Failed to move folder');
    } finally {
      setIsMoving(false);
      setShowConfirmMove(false);
      setMoveData(null);
    }
  };

  const handleCancelMove = () => {
    setShowConfirmMove(false);
    setMoveData(null);
  };

  const getSubfolders = (parentId: string | null) => {
    const subfolders = folders.filter((f: any) => {
      // For root level (parentId === null), show folders with no parent or parent_id === null
      if (parentId === null) {
        return (f.parent_id === null || f.parent_id === undefined || f.parent_id === '') && !f.is_deleted;
      }
      // For nested folders, match the parent_id
      return f.parent_id === parentId && !f.is_deleted;
    });
    return subfolders;
  };

  const renderFolderTree = (parentId: string | null = null, level: number = 0) => {
    const subfolders = getSubfolders(parentId);
    
    return subfolders.map((folder: any) => {
      const childFolders = getSubfolders(folder.id);
      const hasChildren = childFolders.length > 0;
      const isExpanded = expandedFolders.has(folder.id);
      const isDragging = draggedFolder === folder.id;
      const isDropTarget = dropTarget === folder.id;

      const isRootFolder = !folder.parent_id || folder.parent_id === null || folder.parent_id === '';
      const folderClass = isRootFolder ? 'root-folder' : 'child-folder';
      const isActive = currentFolderId === folder.id && currentView === 'all';
      const isInPath = isInActivePath(folder.id);

      return (
        <li key={folder.id} className="nav-item">
          <a
            href="#"
            className={`nav-link ${folderClass} ${isActive ? 'active' : ''} ${isInPath && !isActive ? 'in-path' : ''} ${isDragging ? 'dragging' : ''} ${isDropTarget ? 'drop-target' : ''}`}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            title={folder.name.length > 20 ? folder.name : undefined} // Add tooltip for long names
            draggable
            onDragStart={(e) => handleDragStart(folder.id, e)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(folder.id, e)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(folder.id, e)}
            onClick={(e) => {
              e.preventDefault();
              handleFolderClick(folder.id, hasChildren);
            }}
            onDoubleClick={(e) => {
              e.preventDefault();
              handleFolderDoubleClick(folder.id, hasChildren);
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', paddingLeft: `${level * 0.3}rem`, flex: 1, minWidth: 0 }}>
              <FontAwesomeIcon
                icon={isExpanded ? faFolderOpen : faFolder}
                className="mr-2"
                style={{ flexShrink: 0 }}
              />
              <span className="folder-name" title={folder.name.length > 15 ? folder.name : undefined}>
                {folder.name}
              </span>
            </span>
            {hasChildren && (
              <span 
                className={`folder-toggle ${isExpanded ? 'expanded' : ''}`}
                onClick={(e) => toggleFolderExpand(folder.id, e)}
                style={{ flexShrink: 0 }}
              >
                <FontAwesomeIcon 
                  icon={isExpanded ? faChevronDown : faChevronLeft}
                  style={{ fontSize: '0.75rem' }}
                />
              </span>
            )}
          </a>
          {hasChildren && isExpanded && (
            <ul className="nav nav-treeview" style={{ display: 'block', paddingLeft: '0', listStyle: 'none' }}>
              {renderFolderTree(folder.id, level + 1)}
            </ul>
          )}
        </li>
      );
    });
  };

  return (
    <div className="dropbox-sidebar-pro">
      <ul className="nav nav-pills nav-sidebar flex-column">
        <li className="nav-item">
          <a
            href="#"
            className={`nav-link ${currentView === 'all' && !currentFolderId ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              handleViewClick('all');
            }}
          >
            <FontAwesomeIcon icon={faHome} className="mr-2" />
            All Files
          </a>
        </li>
        
        <li className="nav-item">
          <a 
            href="#" 
            className={`nav-link ${currentView === 'recent' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              handleViewClick('recent');
            }}
          >
            <FontAwesomeIcon icon={faClock} className="mr-2" />
            Recent
          </a>
        </li>

        <li className="nav-item">
          <a 
            href="#" 
            className={`nav-link ${currentView === 'favorites' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              handleViewClick('favorites');
            }}
          >
            <FontAwesomeIcon icon={faStar} className="mr-2" />
            Favourites
          </a>
        </li>

        <li className="nav-item">
          <a 
            href="#" 
            className={`nav-link ${currentView === 'trash' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              handleViewClick('trash');
            }}
          >
            <FontAwesomeIcon icon={faTrash} className="mr-2" />
            Trash
          </a>
        </li>

        <li className="nav-header">FOLDERS</li>
        
        {renderFolderTree(null, 0)}
      </ul>

      {showConfirmMove && moveData && (
        <ConfirmMoveModal
          sourceFolderName={folders.find((f: any) => f.id === moveData.sourceId)?.name || ''}
          targetFolderName={folders.find((f: any) => f.id === moveData.targetId)?.name || ''}
          sourceFolderId={moveData.sourceId}
          targetFolderId={moveData.targetId}
          onConfirm={handleConfirmMove}
          onCancel={handleCancelMove}
          isLoading={isMoving}
        />
      )}
    </div>
  );
};

export default DropboxSidebarPro;
